import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COACHING_BLOCKER_KEYS,
  COACHING_BLOCKER_LABELS,
  COACHING_FOCUS_KEYS,
  COACHING_FOCUS_LABELS,
  COACHING_NEED_KEYS,
  COACHING_NEED_LABELS,
  COACHING_RITUAL_MINUTES,
  COACHING_RITUAL_MODULE_KEYS,
  COACHING_RITUAL_MODULE_LABELS,
  COACHING_STYLES,
  COACHING_STYLE_KEYS,
  type CoachingBlocker,
  type CoachingFocusCategory,
  type CoachingNeed,
  type CoachingRitualModule,
  type CoachingStyle,
} from '@mlm/shared/coaching';
import { BirthDetailsFields } from '../components/birth-details';
import { StepFooter, StepRail } from '../components/step-rail';
import { birthDraftError, birthDraftFrom, birthPatch } from '../lib/birth';
import {
  Chip,
  CoachButton,
  ErrorNote,
  Eyebrow,
  Field,
  inputClass,
  Loading,
} from '../components/primitives';
import { coaching, wallet, type CoachingProfile, type Goal } from '../lib/api';
import { browserTimeZone } from '../lib/format';
import {
  ONBOARDING_STEPS,
  ONBOARDING_STEP_LABELS,
  type OnboardingStep,
} from '../lib/onboarding-steps';
import { qk } from '../lib/queries';

const STEPS = ONBOARDING_STEPS;
type Step = OnboardingStep;
const STEP_LABELS = ONBOARDING_STEP_LABELS;

type StepProps = { profile: CoachingProfile; onDone: () => void; progress: number };

const toggle = <T,>(list: T[], item: T) =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

/** SC-05..SC-10: six short steps, each saved as soon as it is answered. */
export function OnboardingPage() {
  const params = useParams();
  const navigate = useNavigate();
  const step: Step = (STEPS as readonly string[]).includes(params.step ?? '')
    ? (params.step as Step)
    : 'profile';
  const index = STEPS.indexOf(step);
  const profile = useQuery({ queryKey: qk.profile, queryFn: coaching.profile });
  const goals = useQuery({ queryKey: qk.goals('active'), queryFn: () => coaching.goals('active') });
  // A member who already owns a slot (the store's members, updating to this
  // app) has a wallet, orders and codes to get to without the coaching first.
  // A coach-only signup has no slot and simply sees no such link.
  const slots = useQuery({ queryKey: qk.mySlots, queryFn: wallet.slots, staleTime: 5 * 60_000 });
  const hasSlot = (slots.data?.length ?? 0) > 0;

  useEffect(() => {
    if (index === 0) coaching.event('onboarding_started');
  }, [index]);

  if (profile.isPending || goals.isPending) return <Loading />;
  if (profile.isError)
    return <ErrorNote error={profile.error} retry={() => void profile.refetch()} />;
  if (goals.isError) return <ErrorNote error={goals.error} retry={() => void goals.refetch()} />;

  const next = () => {
    coaching.event('onboarding_step_completed', { step });
    // The last answer leads to the paywall (SC-11), which is the only place
    // the app asks for money, and which hands on to the blueprint either way.
    navigate(index < STEPS.length - 1 ? `/onboarding/${STEPS[index + 1]}` : '/paywall');
  };
  const primary = goals.data.find((g) => g.isPrimary) ?? goals.data[0] ?? null;
  const progress = (index + 1) / STEPS.length;
  const shared: StepProps = { profile: profile.data.profile, onDone: next, progress };

  return (
    <div className="lg:grid lg:min-h-screen lg:grid-cols-[360px_minmax(0,1fr)]">
      <StepRail
        title="Your Success Plan"
        steps={STEP_LABELS}
        index={index}
        hrefFor={(i) => `/onboarding/${STEPS[i]}`}
        note="Each answer is saved as soon as you give it, so you can stop and come back."
      />
      <div className="mx-auto flex min-h-screen w-full max-w-xl flex-col px-5 pb-10 pt-6 lg:mx-0 lg:max-w-none lg:px-16 lg:pb-14 lg:pt-[88px] xl:px-[120px]">
        <div className="flex flex-1 flex-col lg:max-w-[640px]">
          <div className="mb-6 flex items-center justify-between lg:hidden">
            <span className="font-display text-lg font-semibold text-ink">Miracles Protocol</span>
            {index > 0 && (
              <Link to={`/onboarding/${STEPS[index - 1]}`} className="eyebrow hover:text-ink">
                Back
              </Link>
            )}
          </div>
          <div className="mb-8 h-px w-full bg-[rgba(16,32,26,0.14)] lg:hidden">
            <div
              className="h-px bg-verde transition-[width] duration-300"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
          <Eyebrow className="mb-3">
            Step {index + 1} of {STEPS.length}
          </Eyebrow>
          {step === 'profile' && <ProfileStep {...shared} />}
          {step === 'focus' && <FocusStep {...shared} />}
          {step === 'goal' && <GoalStep {...shared} goal={primary} />}
          {step === 'assessment' && <AssessmentStep {...shared} />}
          {step === 'ritual' && <RitualStep {...shared} />}
          {step === 'style' && <StyleStep {...shared} />}
          {hasSlot && index === 0 && (
            <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
              Already a member? Your store, wallet and courses are open now; set up the coaching
              whenever you like.{' '}
              <Link to="/shop" className="text-ink underline underline-offset-2">
                Open the store
              </Link>
              {' · '}
              <Link to="/learn" className="text-ink underline underline-offset-2">
                My courses
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function StepHeading({ title, body }: { title: string; body?: string }) {
  return (
    <div className="mb-8 lg:mb-11">
      <h1 className="font-display text-[1.9rem] font-semibold leading-[1.12] text-ink lg:text-[2.5rem] lg:leading-[1.1]">
        {title}
      </h1>
      {body && (
        <p className="mt-3 text-[0.95rem] leading-relaxed text-ink/70 lg:text-base">{body}</p>
      )}
    </div>
  );
}

function useSaveProfile(onDone: () => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: coaching.updateProfile,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.profile });
      onDone();
    },
  });
}

function ProfileStep({ profile, onDone, progress }: StepProps) {
  const [firstName, setFirstName] = useState(profile.firstName ?? '');
  const [birth, setBirth] = useState(() => birthDraftFrom(profile));
  const [error, setError] = useState<string | null>(null);
  const save = useSaveProfile(onDone);
  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        const problem = !firstName.trim() ? 'Tell us your first name.' : birthDraftError(birth);
        setError(problem);
        if (problem) return;
        save.mutate({
          firstName: firstName.trim(),
          timezone: browserTimeZone(),
          ...birthPatch(birth),
        });
      }}
    >
      <StepHeading
        title="Tell us about you"
        body="We use this to personalize your guidance and experience."
      />
      <div className="space-y-5 lg:space-y-7">
        <Field label="First name" htmlFor="first-name">
          <input
            id="first-name"
            className={inputClass}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </Field>
        <BirthDetailsFields value={birth} onChange={setBirth} />
        <p className="text-xs leading-relaxed text-muted-foreground">
          Why do we ask for birth details? They personalize the optional astrology guidance. You can
          skip them and add them later in Settings.
        </p>
      </div>
      {error && (
        <p role="alert" className="mt-5 text-sm text-destructive">
          {error}
        </p>
      )}
      <StepFooter progress={progress}>
        <CoachButton kind="gold" type="submit" block disabled={save.isPending}>
          {save.isPending ? 'Saving' : 'Continue'}
        </CoachButton>
      </StepFooter>
    </form>
  );
}

function FocusStep({ profile, onDone, progress }: StepProps) {
  const [focus, setFocus] = useState<CoachingFocusCategory | null>(
    (profile.focusCategory as CoachingFocusCategory | null) ?? null,
  );
  const save = useSaveProfile(onDone);
  return (
    <div className="flex flex-1 flex-col">
      <StepHeading
        title="What would you most like to improve right now?"
        body="Choose your primary focus. You can add more goals later."
      />
      <div className="grid grid-cols-2 gap-2">
        {COACHING_FOCUS_KEYS.map((key) => (
          <Chip key={key} selected={focus === key} onClick={() => setFocus(key)}>
            {COACHING_FOCUS_LABELS[key]}
          </Chip>
        ))}
      </div>
      <StepFooter progress={progress}>
        <CoachButton
          kind="gold"
          block
          disabled={!focus || save.isPending}
          onClick={() => focus && save.mutate({ focusCategory: focus })}
        >
          Continue
        </CoachButton>
      </StepFooter>
    </div>
  );
}

function GoalStep({
  profile,
  goal,
  onDone,
  progress,
}: StepProps & {
  goal: Goal | null;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(goal?.title ?? '');
  const [targetDate, setTargetDate] = useState(goal?.targetDate ?? '');
  const [why, setWhy] = useState(goal?.reasonWhy ?? '');
  const category = (profile.focusCategory as CoachingFocusCategory | null) ?? undefined;
  const save = useMutation({
    mutationFn: () =>
      goal
        ? coaching.updateGoal(goal.id, {
            title: title.trim(),
            targetDate: targetDate || null,
            reasonWhy: why.trim() || null,
            category: category ?? null,
          })
        : coaching.createGoal({
            title: title.trim(),
            targetDate: targetDate || null,
            reasonWhy: why.trim() || undefined,
            category,
            isPrimary: true,
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.root });
      onDone();
    },
  });
  const tomorrow = new Date(Date.now() + 86_400_000).toISOString().slice(0, 10);
  return (
    <form
      className="flex flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (title.trim().length >= 3) save.mutate();
      }}
    >
      <StepHeading
        title="What do you want to achieve?"
        body="Describe your goal as clearly as you can."
      />
      <div className="space-y-5 lg:space-y-7">
        <Field
          label="Your goal"
          htmlFor="goal"
          hint="Example: I want to grow my business to 1 million in monthly revenue."
        >
          <textarea
            id="goal"
            rows={3}
            className={inputClass}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />
        </Field>
        <Field label="When would you like to achieve this?" htmlFor="target">
          <input
            id="target"
            type="date"
            min={tomorrow}
            className={inputClass}
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </Field>
        <Field label="Why does this matter to you?" htmlFor="why">
          <textarea
            id="why"
            rows={3}
            className={inputClass}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            maxLength={1000}
          />
        </Field>
      </div>
      <StepFooter progress={progress}>
        <CoachButton
          kind="gold"
          type="submit"
          block
          disabled={title.trim().length < 3 || save.isPending}
        >
          {save.isPending ? 'Saving' : 'Continue'}
        </CoachButton>
      </StepFooter>
    </form>
  );
}

function Scale({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-4">
        <input
          type="range"
          min={1}
          max={10}
          step={1}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-11 flex-1 accent-[#277A0B]"
          aria-label={label}
        />
        <span className="w-8 text-right font-mono text-lg text-ink">{value}</span>
      </div>
    </Field>
  );
}

function AssessmentStep({ profile, onDone, progress }: StepProps) {
  const [satisfaction, setSatisfaction] = useState(profile.assessment.satisfaction ?? 5);
  const [confidence, setConfidence] = useState(profile.assessment.confidence ?? 5);
  const [blockers, setBlockers] = useState<CoachingBlocker[]>(
    (profile.assessment.blockers ?? []) as CoachingBlocker[],
  );
  const save = useSaveProfile(() => {
    coaching.event('assessment_completed');
    onDone();
  });
  return (
    <div className="flex flex-1 flex-col">
      <StepHeading title="Where are you starting from?" />
      <div className="space-y-6">
        <Scale
          label="How satisfied are you with this area of your life today?"
          value={satisfaction}
          onChange={setSatisfaction}
        />
        <Scale
          label="How confident are you that you can achieve your goal?"
          value={confidence}
          onChange={setConfidence}
        />
        <Field label="What's most likely to get in your way?">
          <div className="flex flex-wrap gap-2">
            {COACHING_BLOCKER_KEYS.map((key) => (
              <Chip
                key={key}
                selected={blockers.includes(key)}
                onClick={() => setBlockers(toggle(blockers, key))}
              >
                {COACHING_BLOCKER_LABELS[key]}
              </Chip>
            ))}
          </div>
        </Field>
      </div>
      <StepFooter progress={progress}>
        <CoachButton
          kind="gold"
          block
          disabled={save.isPending}
          onClick={() => save.mutate({ assessment: { satisfaction, confidence, blockers } })}
        >
          Continue
        </CoachButton>
      </StepFooter>
    </div>
  );
}

function RitualStep({ profile, onDone, progress }: StepProps) {
  const [minutes, setMinutes] = useState<5 | 10 | 15 | 20>(
    (profile.ritualPrefs.minutes as 5 | 10 | 15 | 20 | undefined) ?? 10,
  );
  const [modules, setModules] = useState<CoachingRitualModule[]>(
    (profile.ritualPrefs.modules as CoachingRitualModule[] | undefined) ?? [
      'affirmations',
      'gratitude',
    ],
  );
  const save = useSaveProfile(onDone);
  return (
    <div className="flex flex-1 flex-col">
      <StepHeading title="Let's design a routine you can actually follow" />
      <div className="space-y-6">
        <Field label="How much time can you give yourself each morning?">
          <div className="grid grid-cols-4 gap-2">
            {COACHING_RITUAL_MINUTES.map((m) => (
              <Chip key={m} selected={minutes === m} onClick={() => setMinutes(m)}>
                {m === 20 ? '20+ min' : `${m} min`}
              </Chip>
            ))}
          </div>
        </Field>
        <Field label="What should your ritual include?">
          <div className="flex flex-wrap gap-2">
            {COACHING_RITUAL_MODULE_KEYS.map((key) => (
              <Chip
                key={key}
                selected={modules.includes(key)}
                onClick={() => setModules(toggle(modules, key))}
              >
                {COACHING_RITUAL_MODULE_LABELS[key]}
              </Chip>
            ))}
          </div>
        </Field>
      </div>
      <StepFooter progress={progress}>
        <CoachButton
          kind="gold"
          block
          disabled={modules.length === 0 || save.isPending}
          onClick={() => save.mutate({ ritualPrefs: { minutes, modules } })}
        >
          Continue
        </CoachButton>
      </StepFooter>
    </div>
  );
}

function StyleStep({ profile, onDone, progress }: StepProps) {
  const [style, setStyle] = useState<CoachingStyle>(
    (profile.coachingStyle as CoachingStyle | null) ?? 'balanced',
  );
  const [needs, setNeeds] = useState<CoachingNeed[]>(profile.coachingNeeds as CoachingNeed[]);
  const save = useSaveProfile(onDone);
  return (
    <div className="flex flex-1 flex-col">
      <StepHeading title="How should your coach guide you?" />
      <div className="border-t border-border" role="radiogroup" aria-label="Coaching style">
        {COACHING_STYLE_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={style === key}
            onClick={() => setStyle(key)}
            className={`flex w-full items-center justify-between gap-4 border-b border-border px-1 py-4 text-left ${style === key ? 'text-ink' : 'text-ink/70 hover:text-ink'}`}
          >
            <span>
              <span className="block text-[0.98rem]">{COACHING_STYLES[key].label}</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">
                {COACHING_STYLES[key].description}
              </span>
            </span>
            <span
              className={`h-4 w-4 shrink-0 rounded-full border ${style === key ? 'border-verde bg-verde' : 'border-[rgba(16,32,26,0.35)]'}`}
            />
          </button>
        ))}
      </div>
      <div className="mt-6">
        <Field label="What do you need most from your coach?">
          <div className="flex flex-wrap gap-2">
            {COACHING_NEED_KEYS.map((key) => (
              <Chip
                key={key}
                selected={needs.includes(key)}
                onClick={() => setNeeds(toggle(needs, key))}
              >
                {COACHING_NEED_LABELS[key]}
              </Chip>
            ))}
          </div>
        </Field>
      </div>
      <StepFooter progress={progress}>
        <CoachButton
          kind="gold"
          block
          disabled={save.isPending}
          onClick={() => save.mutate({ coachingStyle: style, coachingNeeds: needs })}
        >
          Create my Success Blueprint
        </CoachButton>
      </StepFooter>
    </div>
  );
}
